import { useMemo } from 'react';
import { Box, Btn, Field, Select, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { OrdersTable, type SortField } from './OrdersTable';
import { EmptyState, ErrorState, TableSkeleton } from './States';
import { num } from '../utils/format';
import { type DateRange, type ResolveContext, rangeLabel } from '../utils/dateRange';
import type { ApiError } from '../api/client';
import type { DistributionSlice, OrdersResponse, StatusDefinition } from '../api/types';

/**
 * Orders section: search, status filter, export and the paginated table.
 *
 * The KPI cards above stay mounted while these filters change — filtering the
 * table never blanks the summary.
 */
export interface OrdersSectionProps {
  data: OrdersResponse | null;
  loading: boolean;
  error: ApiError | null;
  catalog: StatusDefinition[];
  distribution: DistributionSlice[];
  pendingStatuses: readonly string[];
  warehouseLabel: string;
  range: DateRange;
  rangeCtx: ResolveContext;

  searchInput: string;
  onSearchInput: (v: string) => void;
  selectedStatuses: string[];
  onToggleStatus: (status: string) => void;
  onClearFilters: () => void;

  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;

  sortBy: SortField;
  sortDir: 'asc' | 'desc';
  onSort: (field: SortField) => void;

  exportUrl: string;
  exportXlsxUrl: string;
  onRetry: () => void;
  latestOrderDate?: string | null;
  /** Widens the range to cover everything the warehouse has. */
  onJumpToLatest?: () => void;
}

export function OrdersSection(props: OrdersSectionProps): JSX.Element {
  const {
    data,
    loading,
    error,
    catalog,
    distribution,
    pendingStatuses,
    warehouseLabel,
    range,
    rangeCtx,
    searchInput,
    onSearchInput,
    selectedStatuses,
    onToggleStatus,
    onClearFilters,
    page,
    pageSize,
    onPage,
    onPageSize,
    sortBy,
    sortDir,
    onSort,
    exportUrl,
    exportXlsxUrl,
    onRetry,
    latestOrderDate,
    onJumpToLatest,
  } = props;

  const counts = useMemo(() => new Map(distribution.map((d) => [d.status, d.count])), [distribution]);
  // Only statuses that actually occurred on this date become chips — the filter
  // bar reflects the day, not the whole enum.
  const chips = useMemo(() => catalog.filter((s) => (counts.get(s.value) ?? 0) > 0), [catalog, counts]);

  const filtered = selectedStatuses.length > 0 || searchInput.trim().length > 0;
  const hasRows = (data?.rows.length ?? 0) > 0;
  const total = data?.total ?? 0;

  return (
    <Box as="section" aria-label="Orders" className="rounded-card bg-surface-card ring-1 ring-hairline shadow-card overflow-hidden">
      {/* Toolbar */}
      <Box className="p-5 flex flex-col gap-4">
        <Box className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <Box className="flex items-baseline gap-2.5">
            <Txt as="h2" className="text-[15px] font-semibold text-ink-primary">
              Orders
            </Txt>
            <Txt className="text-[12px] text-ink-muted tabular" aria-live="polite">
              {loading && !data ? 'Loading…' : `${num(total)} ${total === 1 ? 'order' : 'orders'}`}
              {filtered && data ? ' (filtered)' : ''}
            </Txt>
          </Box>

          <Box className="flex flex-col sm:flex-row gap-2.5 sm:items-center">
            {/* Search */}
            <Box className="relative flex items-center">
              <Box className="absolute left-3 pointer-events-none text-ink-muted">
                <Icon name="search" />
              </Box>
              <Field
                type="search"
                value={searchInput}
                onChangeText={onSearchInput}
                placeholder="Search order ID, customer, AWB…"
                aria-label="Search orders"
                inputMode="search"
                className="h-9 w-full sm:w-[280px] pl-9 pr-3 rounded-lg bg-surface-sunken text-ink-primary text-[13px] ring-1 ring-hairline placeholder:text-ink-muted focus:bg-surface-card transition-colors duration-150"
              />
            </Box>

            {/*
              Export — always visible, respects the active filters.
              Excel is primary because that is what operations opens the file
              in, and CSV loses phone/AWB digits to scientific notation there.
              CSV stays available as the secondary half of the split button.
            */}
            <Box className="inline-flex items-stretch rounded-lg overflow-hidden">
              <a
                href={exportXlsxUrl}
                className="inline-flex items-center justify-center gap-2 h-9 px-3.5 text-[13px] font-semibold bg-ink-primary text-surface-card hover:opacity-90 active:opacity-80 transition-opacity duration-150 no-underline whitespace-nowrap"
                aria-label="Export orders to Excel"
                title={`Export ${filtered ? 'the filtered' : 'these'} orders as an Excel file`}
              >
                <Icon name="download" className="w-4 h-4" />
                Export Excel
              </a>
              <a
                href={exportUrl}
                className="inline-flex items-center justify-center h-9 px-2.5 text-[12px] font-medium bg-ink-primary text-surface-card opacity-70 hover:opacity-100 transition-opacity duration-150 no-underline whitespace-nowrap border-l border-surface-card/25"
                aria-label="Export orders to CSV"
                title="Export as CSV (for scripts and other tools)"
              >
                CSV
              </a>
            </Box>
          </Box>
        </Box>

        {/* Status filter chips */}
        {chips.length > 0 ? (
          <Box className="flex flex-wrap items-center gap-1.5">
            <Btn
              onPress={onClearFilters}
              aria-pressed={selectedStatuses.length === 0}
              className={[
                'px-2.5 py-1 rounded-lg text-[12px] font-medium ring-1 transition-colors duration-150',
                selectedStatuses.length === 0
                  ? 'bg-ink-primary text-surface-card ring-transparent'
                  : 'bg-surface-sunken text-ink-secondary ring-hairline hover:text-ink-primary',
              ].join(' ')}
            >
              All
            </Btn>

            {chips.map((status) => {
              const active = selectedStatuses.includes(status.value);
              const count = counts.get(status.value) ?? 0;
              return (
                <Btn
                  key={status.value}
                  onPress={() => onToggleStatus(status.value)}
                  aria-pressed={active}
                  title={status.description}
                  className={[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium ring-1 transition-colors duration-150',
                    active
                      ? 'bg-ink-primary text-surface-card ring-transparent'
                      : 'bg-surface-sunken text-ink-secondary ring-hairline hover:text-ink-primary',
                  ].join(' ')}
                >
                  {status.label}
                  <Txt className="tabular opacity-70">{num(count)}</Txt>
                </Btn>
              );
            })}

            {filtered ? (
              <Btn
                onPress={onClearFilters}
                aria-label="Clear all filters"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[12px] text-ink-muted hover:text-ink-primary transition-colors duration-150"
              >
                <Icon name="x" className="w-3 h-3" />
                Clear
              </Btn>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {/* Body */}
      {error ? (
        <Box className="p-5">
          <ErrorState message={error.message} code={error.code} onRetry={onRetry} compact />
        </Box>
      ) : loading && !data ? (
        <TableSkeleton />
      ) : hasRows ? (
        /* No dimming while refreshing: with auto-refresh on, a periodic fade
           would flicker the table every interval. Freshness is communicated by
           the "Updated HH:MM" stamp, the Live dot and the spinning refresh icon
           instead. */
        <Box>
          <OrdersTable
            orders={data!.rows}
            catalog={catalog}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            pendingStatuses={pendingStatuses}
          />
        </Box>
      ) : (
        <EmptyState
          rangeText={rangeLabel(range, rangeCtx)}
          warehouseLabel={warehouseLabel}
          filtered={filtered}
          onClearFilters={onClearFilters}
          latestOrderDate={latestOrderDate}
          onJumpToLatest={onJumpToLatest}
        />
      )}

      {/* Pagination */}
      {hasRows && data ? (
        <Box className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-3.5 border-t border-hairline bg-surface-raised">
          <Box className="flex items-center gap-2">
            <Txt className="text-[12px] text-ink-muted">Rows</Txt>
            <Select
              value={String(pageSize)}
              onChange={(v) => onPageSize(Number(v))}
              aria-label="Rows per page"
              options={[
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              className="h-8 px-2 rounded-lg bg-surface-card text-ink-primary text-[12px] ring-1 ring-hairline"
            />
            <Txt className="text-[12px] text-ink-muted tabular">
              {(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} of{' '}
              {num(data.total)}
            </Txt>
          </Box>

          <Box className="flex items-center gap-1.5">
            <Btn
              onPress={() => onPage(page - 1)}
              disabled={data.page <= 1}
              aria-label="Previous page"
              className="grid place-items-center w-8 h-8 rounded-lg bg-surface-card text-ink-secondary ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-35 disabled:pointer-events-none"
            >
              <Icon name="chevron-left" className="w-4 h-4" />
            </Btn>
            <Txt className="px-2 text-[12px] text-ink-secondary tabular">
              Page {data.page} of {data.pageCount}
            </Txt>
            <Btn
              onPress={() => onPage(page + 1)}
              disabled={data.page >= data.pageCount}
              aria-label="Next page"
              className="grid place-items-center w-8 h-8 rounded-lg bg-surface-card text-ink-secondary ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-35 disabled:pointer-events-none"
            >
              <Icon name="chevron-right" className="w-4 h-4" />
            </Btn>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
